import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  TextField,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Slide,
  Typography,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import { EasyCheckButton } from '../../common/Controls';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock,
  LoginRounded 
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { themeUtils } from '../../../theme';
import ForgotPasswordForm from './ForgotPasswordForm';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: themeUtils.gradients.backgroundLight,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: themeUtils.gradients.primary,
    opacity: 0.05,
    zIndex: 0,
  }
}));

const LoginCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(5),
  boxShadow: `0 20px 60px rgba(37, 99, 235, 0.15)`,
  border: `1px solid ${themeUtils.colors.blue['blue-100']}`,
  maxWidth: '420px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  margin: theme.spacing(2),
}));

const LogoSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const LogoEasy = styled('span')(({ theme }) => ({
  color: '#0f172a', // Dark gray/black
}));

const LogoCheck = styled('span')(({ theme }) => ({
  color: '#2563eb', // Blue
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '1.75rem',
  marginBottom: theme.spacing(1),
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}20`,
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
    },
    '& fieldset': {
      borderColor: themeUtils.colors.blue['blue-100'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: themeUtils.colors.blue['blue-300'],
    },
    '&.Mui-focused fieldset': {
      borderColor: themeUtils.colors.blue['blue-600'],
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: themeUtils.colors.blue['blue-600'],
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '0 8px',
      marginLeft: '-4px',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiCheckbox-root': {
    color: themeUtils.colors.blue['blue-400'],
    '&.Mui-checked': {
      color: themeUtils.colors.blue['blue-600'],
    },
    '&:hover': {
      backgroundColor: `${themeUtils.colors.blue['blue-50']}`,
    },
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
}));

const ForgotPasswordLink = styled(Link)(({ theme }) => ({
  color: themeUtils.colors.blue['blue-600'],
  textDecoration: 'none',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: themeUtils.colors.blue['blue-700'],
  },
}));

const SignUpSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  borderTop: `1px solid ${themeUtils.colors.blue['blue-100']}`,
}));

const AnimatedBox = styled(Box)(({ theme }) => ({
  animation: 'fadeInUp 0.6s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const LoginForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useEasyCheck();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  // Handle URL parameters for success messages
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');
    const email = urlParams.get('email');

    if (message === 'account_created') {
      setSuccessMessage('Account created successfully! Please check your email for verification and then sign in.');
      
      // Pre-fill email if provided
      if (email) {
        setValue('email', decodeURIComponent(email));
      }

      // Clear URL parameters after showing message
      navigate('/login', { replace: true });
    } else if (message === 'password_reset_success') {
      setSuccessMessage('Password reset successfully! You can now sign in with your new password.');
      
      // Clear URL parameters after showing message
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate, setValue]);

  const onSubmit = async (credentials) => {
    try {
      setLoading(true);
      setError('');
      const result = await login(credentials);
      if (result.success) {
        if (result.requiresOtp) {
          // Navigate to OTP verification, passing credentials via state
          navigate('/verify-otp', {
            state: { email: credentials.email, password: credentials.password }
          });
        } else {
          navigate(result.redirectUrl || '/');
        }
      } else {
        setError(result.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setError('');
    setSuccessMessage('');
  };

  // Show Forgot Password form if requested
  if (showForgotPassword) {
    return (
      <LoginContainer>
        <ForgotPasswordForm onBackToLogin={handleBackToLogin} />
        
        {/* Copyright text positioned at bottom of screen */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: 20, 
          left: 0, 
          right: 0, 
          textAlign: 'center' 
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            © 2025 EasyCheck. All rights reserved.{process.env.REACT_APP_BUILD_TIME && ` | Build: ${new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString()}`}
          </Typography>
        </Box>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LoginCard>
        <AnimatedBox>
          <LogoSection>
            <LogoContainer>
              <LogoText>
                <LogoEasy>Easy</LogoEasy>
                <LogoCheck>Check</LogoCheck>
              </LogoText>
            </LogoContainer>
            <SubtitleText>Sign in to your EasyCheck account</SubtitleText>
          </LogoSection>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {successMessage && (
                <Slide direction="down" in={!!successMessage} mountOnEnter unmountOnExit>
                  <Alert 
                    severity="success" 
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      border: '1px solid rgba(76, 175, 80, 0.2)',
                      backdropFilter: 'blur(10px)',
                    }}
                    onClose={() => setSuccessMessage('')}
                  >
                    {successMessage}
                  </Alert>
                </Slide>
              )}

              {error && (
                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.2)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

              <Controller
                name="email"
                control={control}
                rules={{ 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ 
                              color: themeUtils.colors.blue['blue-600'],
                              opacity: 0.7,
                              '&:hover': {
                                backgroundColor: `${themeUtils.colors.blue['blue-50']}`,
                                opacity: 1,
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <StyledFormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Remember me"
                />
                <ForgotPasswordLink onClick={() => setShowForgotPassword(true)}>
                  Forgot password?
                </ForgotPasswordLink>
              </Box>

              <EasyCheckButton
                buttonType="auth"
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                size="large"
                startIcon={<LoginRounded />}
                sx={{
                  background: themeUtils.gradients.primary,
                  '&:hover': {
                    background: themeUtils.gradients.primaryDark,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </EasyCheckButton>
            </Stack>
          </Box>

          <SignUpSection>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => navigate('/signup')}
                sx={{ 
                  color: themeUtils.colors.blue['blue-600'],
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: themeUtils.colors.blue['blue-700'],
                  }
                }}
              >
                Sign up
              </Link>
            </Typography>
          </SignUpSection>

        </AnimatedBox>
      </LoginCard>
      
      {/* Copyright text positioned at bottom of screen */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 20, 
        left: 0, 
        right: 0, 
        textAlign: 'center' 
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          © 2025 EasyCheck. All rights reserved.{process.env.REACT_APP_BUILD_TIME && ` | Build: ${new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString()}`}
        </Typography>
      </Box>
    </LoginContainer>
  );
};

export default LoginForm; 